/**
 * What the user does with a file linked where it is (plan 14): Open in its
 * default app (or, for what would run something, shown in Finder or
 * Explorer instead), Show in Finder or Explorer, Locate… a file that moved,
 * and on macOS let Denker into the folder it is in. Each tells the card
 * what it found (`local-file-problem-store`).
 */
import { updateFrame } from "@/api/spaces";
import {
  localFileProblem,
  openLocalFile,
  relinkLocalFile,
  revealLocalFile,
  type LinkRef,
  type ThisComputer,
} from "@/desktop/local-files";
import { openPrivacyPaneForCurrentMacOS } from "@/desktop/onboarding/permissions-deep-links";
import { baseName, pickNativePaths } from "@/desktop/pick-native-files";
import { translate } from "@/i18n";
import { framesFileMessages } from "@/i18n/messages/frames-file";
import { fileToast as toast } from "@/lib/file-toast";
import { applyFramePatch } from "@/lib/frame-ingest";
import { linkedFileContent, type LocalLink } from "@/lib/local-file-link";
import { useLocalFileProblemStore } from "@/stores/local-file-problem-store";
import { capturePdfThumbnail } from "./pdf-thumbnail";

/** A linked file as its frame shows it: what each action names. */
export interface FrameLink {
  link: LocalLink;
  frameId: string;
}

function linkRef({ link, frameId }: FrameLink): LinkRef {
  return { linkId: link.linkId, frameId };
}

/**
 * Runs `action` on the file and records what it found; a problem the card
 * cannot show (anything but missing or denied) is said in a toast.
 */
async function run<T>(
  target: FrameLink,
  action: () => Promise<T>,
): Promise<T | null> {
  const { report } = useLocalFileProblemStore.getState();
  const { linkId, path } = target.link;
  try {
    const result = await action();
    report(linkId, null);
    return result;
  } catch (error) {
    const problem = localFileProblem(error);
    report(linkId, problem);
    if (
      problem === "failed" ||
      problem === "too-large" ||
      problem === "not-pdf"
    ) {
      toast(
        "error",
        translate(framesFileMessages, "fileOpenFailed", {
          name: baseName(path),
        }),
      );
    }
    return null;
  }
}

/** Opens the file; one that would run something is shown, and says so. */
export async function openLinkedFile(target: FrameLink): Promise<void> {
  if (
    (await run(target, () => openLocalFile(linkRef(target)))) === "revealed"
  ) {
    toast(
      "info",
      translate(framesFileMessages, "fileShownInstead", {
        name: baseName(target.link.path),
      }),
    );
  }
}

/** Shows the file in Finder or Explorer. */
export async function revealLinkedFile(target: FrameLink): Promise<void> {
  await run(target, () => revealLocalFile(linkRef(target)));
}

/**
 * Locate… (a missing file) or Link a file… (no link here): the user picks
 * the file; the frame's own link follows it, or a fresh link is made for
 * this frame, the frame names its place (and name), and a PDF's page 1 is
 * drawn again from it.
 */
export async function locateLinkedFile(
  target: FrameLink,
  projectId: string,
  computer: ThisComputer,
): Promise<void> {
  const [path] = await pickNativePaths({ multiple: false });
  if (!path) return;
  const located = await run(target, () =>
    relinkLocalFile(linkRef(target), path),
  );
  if (!located) return;
  const frameLink = { linkId: located.linkId, frameId: target.frameId };
  // A linked file's content is exactly its link: nothing else to keep.
  const content = linkedFileContent(located, computer);
  try {
    const saved = await updateFrame(projectId, target.frameId, { content });
    applyFramePatch(target.frameId, { content: saved.content });
  } catch {
    toast(
      "error",
      translate(framesFileMessages, "fileOpenFailed", { name: located.name }),
    );
    return;
  }
  if (content.kind === "pdf") {
    await capturePdfThumbnail(
      projectId,
      target.frameId,
      { kind: "local", ...frameLink },
      "",
    );
  }
}

/** macOS: Privacy & Security › Files and Folders, to let Denker in. */
export function allowFolderAccess(): void {
  void openPrivacyPaneForCurrentMacOS("files_folders").catch(() => undefined);
}
